import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Crosshair, ChevronLeft, Plus, Search, Route, Users2, ExternalLink, X, GitBranch,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState, Badge, Btn, Input, Select } from "@/components/common";
import NetworkGraph from "@/components/NetworkGraph";
import ConnectionModal from "@/components/ConnectionModal";

const REL_TYPES = ["knows", "spouse", "partner", "family", "friend", "colleague", "introduced_by", "works_for", "owns", "member_of", "neighbor_of", "supplies", "collaborates_with", "participated_same_event", "located_beside", "participates_in", "links_to", "other"];
const VERIFS = ["unverified", "uncertain", "confirmed", "disputed", "outdated"];

export default function ContactWeb({ entityId, entityName, workspaceId, canEdit, onOpenEntity, onChanged }) {
  const [centerId, setCenterId] = useState(entityId);
  const [stack, setStack] = useState([]); // for "return to previous view"
  const [web, setWeb] = useState(null);
  const [degrees, setDegrees] = useState(1);
  const [filters, setFilters] = useState({ rel_type: "", verification: "", origin_id: "", strategic_interest_id: "" });
  const [origins, setOrigins] = useState([]);
  const [interests, setInterests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pathA, setPathA] = useState(null);
  const [pathB, setPathB] = useState(null);
  const [pathResult, setPathResult] = useState(null);
  const [shared, setShared] = useState(null);
  const [relSearch, setRelSearch] = useState("");
  const [connOpen, setConnOpen] = useState(false);

  useEffect(() => { setCenterId(entityId); setStack([]); }, [entityId]);

  useEffect(() => {
    api.get(`/origins?workspace_id=${workspaceId}&limit=100`).then((r) => setOrigins(r.data.items)).catch(() => {});
    api.get(`/strategic-interests?workspace_id=${workspaceId}`).then((r) => setInterests(r.data)).catch(() => {});
  }, [workspaceId]);

  const load = useCallback(async () => {
    if (!centerId) return;
    const p = new URLSearchParams({ workspace_id: workspaceId, degrees, rel_type: filters.rel_type, verification: filters.verification, origin_id: filters.origin_id, strategic_interest_id: filters.strategic_interest_id });
    const { data } = await api.get(`/entities/${centerId}/web?${p}`);
    setWeb(data);
  }, [centerId, workspaceId, degrees, filters]);

  useEffect(() => { setWeb(null); setPathResult(null); setShared(null); load(); }, [load]);

  const recenter = (node) => {
    if (node.id === centerId) return;
    setStack((s) => [...s, centerId]);
    setCenterId(node.id);
    setSelected(null);
  };
  const back = () => {
    setStack((s) => {
      if (s.length === 0) return s;
      const prev = s[s.length - 1];
      setCenterId(prev);
      setSelected(null);
      return s.slice(0, -1);
    });
  };

  const runPath = async () => {
    if (!pathA || !pathB) return toast.error("Pick two contacts (A and B)");
    try {
      const { data } = await api.get(`/graph/path?workspace_id=${workspaceId}&from_id=${pathA.id}&to_id=${pathB.id}`);
      if (!data.found) { setPathResult({ found: false }); toast.message("No documented path between these contacts"); }
      else setPathResult(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const runShared = async () => {
    if (!pathA || !pathB) return toast.error("Pick two contacts (A and B)");
    try {
      const { data } = await api.get(`/graph/shared?workspace_id=${workspaceId}&a_id=${pathA.id}&b_id=${pathB.id}`);
      setShared(data.shared);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const highlightIds = pathResult?.found ? pathResult.path.map((p) => p.id) : [];

  const filteredLinks = useMemo(() => {
    if (!web) return [];
    const list = web.links.map((l) => ({ ...l }));
    if (!relSearch) return list;
    const nameOf = (id) => web.nodes.find((n) => n.id === id)?.label || "";
    return list.filter((l) => `${l.label} ${nameOf(l.source)} ${nameOf(l.target)}`.toLowerCase().includes(relSearch.toLowerCase()));
  }, [web, relSearch]);

  const nameOf = (id) => web?.nodes.find((n) => n.id === id)?.label || id?.slice(0, 6);

  return (
    <div className="flex flex-col lg:flex-row gap-0" style={{ border: "1px solid var(--si-border)", borderRadius: 2, minHeight: 560 }}>
      {/* graph area */}
      <div className="flex-1 flex flex-col min-w-0" style={{ borderRight: "1px solid var(--si-border)" }}>
        {/* controls */}
        <div className="flex items-center gap-2 px-3 py-2 flex-wrap" style={{ borderBottom: "1px solid var(--si-border)", background: "var(--si-surface)" }}>
          {stack.length > 0 && <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={back} data-testid="web-back"><ChevronLeft size={14} /> Back</Btn>}
          <span className="si-label flex items-center gap-1"><Crosshair size={12} /> {web?.center?.name || entityName}</span>
          <div className="flex" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
            {[1, 2, 3].map((d) => (
              <button key={d} data-testid={`web-degree-${d}`} onClick={() => setDegrees(d)} className="px-2.5 py-1 text-xs font-mono transition-colors duration-200"
                style={{ background: degrees === d ? "var(--si-surface-hover)" : "transparent", color: degrees === d ? "var(--si-text)" : "var(--si-text-2)" }}>{d}°</button>
            ))}
          </div>
          <Select value={filters.rel_type} onChange={(e) => setFilters((f) => ({ ...f, rel_type: e.target.value }))} style={{ width: "auto" }} data-testid="web-filter-rel"><option value="">All relationships</option>{REL_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</Select>
          <Select value={filters.verification} onChange={(e) => setFilters((f) => ({ ...f, verification: e.target.value }))} style={{ width: "auto" }}><option value="">All verification</option>{VERIFS.map((t) => <option key={t} value={t}>{t}</option>)}</Select>
          <Select value={filters.origin_id} onChange={(e) => setFilters((f) => ({ ...f, origin_id: e.target.value }))} style={{ width: "auto" }}><option value="">All origins</option>{origins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</Select>
          <Select value={filters.strategic_interest_id} onChange={(e) => setFilters((f) => ({ ...f, strategic_interest_id: e.target.value }))} style={{ width: "auto" }} data-testid="web-filter-si"><option value="">All interests</option>{interests.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
          {canEdit && <Btn variant="secondary" className="!py-1 !px-2 text-xs" onClick={() => setConnOpen(true)} data-testid="web-add-connection"><Plus size={13} /> Add connection</Btn>}
        </div>

        <div className="relative" style={{ height: 480 }}>
          {!web ? <Loading /> : web.nodes.length <= 1 ? (
            <EmptyState icon={GitBranch} title="No connections yet"
              hint="This contact has no documented connections. Use 'Add connection' to link it to another contact."
              action={canEdit && <Btn variant="primary" onClick={() => setConnOpen(true)}><Plus size={15} /> Add connection</Btn>} />
          ) : (
            <NetworkGraph data={{ nodes: web.nodes, links: web.links }} selectedId={selected?.id} highlightPath={highlightIds} onSelect={setSelected} />
          )}
          {web?.truncated && (
            <div className="absolute top-2 right-2 px-2 py-1 text-xs font-mono" style={{ background: "var(--si-surface)", border: "1px solid var(--si-warning)", color: "var(--si-warning)", borderRadius: 2 }}>
              bounded view · showing first {web.node_count} contacts
            </div>
          )}
        </div>

        {/* selected node actions */}
        {selected && (
          <div className="flex items-center gap-2 px-3 py-2 flex-wrap" style={{ borderTop: "1px solid var(--si-border)", background: "var(--si-surface)" }}>
            <span className="text-sm" style={{ color: "var(--si-text)" }}>{selected.label}</span>
            <Badge value={selected.verification_status} />
            {selected.id !== centerId && <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => recenter(selected)} data-testid="web-recenter"><Crosshair size={13} /> Recenter</Btn>}
            <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => onOpenEntity(selected.id)} data-testid="web-open-file"><ExternalLink size={13} /> Open file</Btn>
            <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => setPathA(selected)}>Set as A</Btn>
            <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => setPathB(selected)}>Set as B</Btn>
            <button onClick={() => setSelected(null)} style={{ color: "var(--si-text-2)" }}><X size={14} /></button>
          </div>
        )}
      </div>

      {/* side: relationship list + path + shared */}
      <aside className="flex flex-col" style={{ width: 320, maxWidth: "100%" }}>
        {/* path tools */}
        <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
          <div className="si-label mb-2 flex items-center gap-1"><Route size={12} /> Path & shared ties</div>
          <div className="text-xs font-mono mb-2" style={{ color: "var(--si-text-2)" }}>
            A: <span style={{ color: "var(--si-text)" }}>{pathA?.label || "—"}</span><br />
            B: <span style={{ color: "var(--si-text)" }}>{pathB?.label || "—"}</span>
          </div>
          <div className="flex gap-2">
            <Btn variant="secondary" className="!py-1 !px-2 text-xs" onClick={runPath} data-testid="web-run-path"><Route size={13} /> Path</Btn>
            <Btn variant="secondary" className="!py-1 !px-2 text-xs" onClick={runShared} data-testid="web-run-shared"><Users2 size={13} /> Shared</Btn>
          </div>
          {pathResult && (pathResult.found
            ? <div className="mt-2 text-xs p-2" style={{ background: "var(--si-bg)", border: "1px solid var(--si-teal)", borderRadius: 2, color: "var(--si-silver)" }}>
                {pathResult.path.map((p, i) => <span key={p.id}>{i > 0 && " → "}<span style={{ color: "var(--si-text)" }}>{p.name}</span></span>)}
                <div className="si-label mt-1">documented sequence · not an endorsement or permission to contact</div>
              </div>
            : <div className="mt-2 text-xs" style={{ color: "var(--si-warning)" }}>No documented path.</div>)}
          {shared && <div className="mt-2 text-xs"><span className="si-label">shared ({shared.length}): </span><span style={{ color: "var(--si-text)" }}>{shared.map((s) => s.name).join(", ") || "none"}</span></div>}
        </div>

        {/* relationship list */}
        <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: "var(--si-text-2)" }} />
            <Input value={relSearch} onChange={(e) => setRelSearch(e.target.value)} placeholder="Search relationships…" style={{ paddingLeft: 26 }} data-testid="web-rel-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 360 }}>
          {filteredLinks.length === 0 ? <div className="text-sm px-4 py-6 text-center" style={{ color: "var(--si-text-2)" }}>No relationships.</div> : (
            filteredLinks.map((l) => (
              <div key={l.id} className="px-4 py-2.5 text-xs" style={{ borderBottom: "1px solid var(--si-border)" }}>
                <div style={{ color: "var(--si-text)" }}>
                  {nameOf(l.source)} <span className="font-mono mx-1" style={{ color: "var(--si-silver)" }}>{l.direction === "mutual" ? "↔" : "→"} {l.label}</span> {nameOf(l.target)}
                </div>
                <div className="mt-0.5"><Badge value={l.status} /></div>
              </div>
            ))
          )}
        </div>
      </aside>

      {connOpen && web?.center && (
        <ConnectionModal workspaceId={workspaceId} source={{ id: web.center.id, name: web.center.name }}
          onClose={() => setConnOpen(false)} onSaved={() => { setConnOpen(false); load(); onChanged && onChanged(); }} />
      )}
    </div>
  );
}
